import { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { usePageTitle } from '@/hooks/usePageTitle'
import { ProgramFaq, type FaqItem } from '@/features/program/ProgramFaq'
import { CAREER_INTERNSHIP, useProgramVacancy } from '@/features/program/queries'

/** Only the first FAQ group differs between the two program pages. */
const TBIGIN_FAQS: FaqItem[] = [
  {
    question: 'What is the minimum education requirement for candidates?',
    answer:
      'This program is open to students and fresh graduates (min. associate degree (D3) from any major)',
  },
  { question: 'How long is the internship period?', answer: 'The interenship lasts for 6 months' },
  { question: ' Is the intereship paid? ', answer: "Yes. It's a paid interenship program" },
  {
    question: 'Where will the interenship take place?',
    answer: 'The interenship will be conducted at our head office in Kuningan, South Jakarta',
  },
]

const ACTIVITIES = [
  { image: 'Opening.jpg', alt: 'Onboarding', caption: 'Onboarding Session' },
  { image: 'Sharing session.jpg', alt: 'Sharing', caption: 'Sharing Session' },
  { image: 'Skill Up Program.jpg', alt: 'Skill Up', caption: 'TBIG In. Skill Up Program' },
  { image: 'Fun Meeting.jpg', alt: 'Skill Up', caption: 'Fun Meeting' },
  { image: 'Assessment.jpg', alt: 'Skill Up', caption: 'TBIG In. Assessment' },
  { image: 'Final Presentation.jpg', alt: 'Skill Up', caption: 'Panel Presentation' },
]

/**
 * Port of Views/Program/TBIGIN.cshtml.
 *
 * BUG FIXED, deliberately: the Apply button linked to
 * `/Vacancy/Form/@Model.trxVacancyID` — a RAW integer id on a route that expects
 * the AES-encoded one — so every click threw in DecodeId and bounced the
 * candidate to the vacancy listing instead of opening the internship form. It
 * now uses the encoded id. TLDP.cshtml had the identical defect.
 */
export function ProgramTBIGIn(): JSX.Element {
  usePageTitle('TBIGIn Page')
  const { data } = useProgramVacancy(CAREER_INTERNSHIP)

  const trackRef = useRef<HTMLDivElement>(null)
  const [index, setIndex] = useState(0)

  /**
   * The page's inline slideRight(): shift the track by one card, wrapping to the
   * start once the last card is flush right. Visible-card count is
   * breakpoint-dependent (3 / 2 / 1), and the +20px is the gap between cards.
   * The original read `cards[0].offsetWidth` on every call, so the measurement
   * stays in the DOM here rather than being tracked in state.
   */
  const visibleCount = (): number => {
    if (window.innerWidth >= 992) return 3
    if (window.innerWidth >= 768) return 2
    return 1
  }

  const applyOffset = useCallback((nextIndex: number): void => {
    const track = trackRef.current
    const firstCard = track?.querySelector<HTMLElement>('.card-carousel')
    if (!track || !firstCard) return
    track.style.transform = `translateX(-${nextIndex * (firstCard.offsetWidth + 20)}px)`
  }, [])

  const slideRight = (): void => {
    const maxIndex = ACTIVITIES.length - visibleCount()
    const nextIndex = index < maxIndex ? index + 1 : 0
    setIndex(nextIndex)
    applyOffset(nextIndex)
  }

  // "Reset posisi saat resize layar" — re-measure at the current index.
  useEffect(() => {
    const onResize = (): void => applyOffset(index)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [index, applyOffset])

  return (
    <>
      <section className="bg-white pt-0 pb-0">
        <div className="container">
          <div className="row justify-content-center text-center">
            {data?.encodedTrxVacancyID && (
              <Link
                to={`/Vacancy/Form/${data.encodedTrxVacancyID}`}
                className="btn btn-info overlay-button mt-3"
              >
                Apply Now
              </Link>
            )}
            <img src="/lib/images/landing/TBIGIn/1.png" alt="" />
          </div>
        </div>
      </section>

      {['2.png', '3.png', '4.png'].map((image) => (
        <section key={image} className="bg-white pt-0 pb-0">
          <div className="container">
            <div className="row justify-content-center text-center">
              <img src={`/lib/images/landing/TBIGIn/${image}`} alt="" />
            </div>
          </div>
        </section>
      ))}

      <section className="bg-white pt-0">
        <div className="container pt-5">
          <div className="row justify-content-center">
            <div className="col-md-12 col-xl-4">
              <h1
                className="wow animate__fadeInUp section-title text-TBG large-font text-TBG-center"
                data-wow-delay="0.2s"
                style={{ marginTop: 30 }}
              >
                TBIG In.
              </h1>
              <h1
                className="wow animate__fadeInUp section-title text-TBG large-font text-TBG-center"
                data-wow-delay="0.2s"
              >
                Activities
              </h1>
              <div className="swipe-wrapper">
                <div className="swipe-box">
                  <span className="swipe-text">Swipe right</span>
                  <button type="button" className="swipe-arrow" onClick={slideRight}>
                    ➡
                  </button>
                </div>
              </div>
            </div>
            <div className="col-md-12 col-xl-8">
              <div className="d-flex align-items-center carousel-wrapper">
                <div className="carousel-container position-relative flex-grow-1">
                  <div className="carousel-track" id="carouselTrack" ref={trackRef}>
                    {ACTIVITIES.map((activity) => (
                      <div key={activity.image} className="card-carousel">
                        <div className="card-slide-img border-0 bg-transparent">
                          <img
                            src={`/lib/images/landing/TBIGIn/${activity.image}`}
                            alt={activity.alt}
                          />
                          <div className="card-content-slide-img">{activity.caption}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <ProgramFaq idPrefix="tbigin" programFaqs={TBIGIN_FAQS} />
    </>
  )
}
